import { apiClient } from '../axiosInstance';
import { payClient } from '../payClient';
import { ENDPOINTS } from '../endpoints';
import {
  rememberInvoicePayload,
  rememberInvoiceRows,
  rememberPayExchangePayload,
} from '../publicIds';
import type {
  InvoiceDetail,
  InvoiceReportParams,
  InvoiceReportResponse,
  InvoiceService,
  PayExchangeWire,
  PaymentMethodOption,
  TransactionRequestBody,
  TransactionResult,
} from './interfaces/InvoiceService';
import {
  adaptInvoiceReportResponse,
  type InvoiceReportResponseWire,
} from './adapters/invoiceRow';
import {
  adaptInvoiceDetail,
  type InvoiceDetailWire,
} from './adapters/invoiceDetail';
import { buildSignPayload, SIGN_REQUEST_TIMEOUT_MS } from '../../utils/signing';
import {
  adaptPaymentMethods,
  type PaymentMethodWire,
} from './adapters/payment-methods';

/**
 * Shape of the `201` transaction-create response body (subset the success
 * screen reads). On an idempotent replay the server renders an EMPTY body
 * (`render body: nil, status: :created`) — `data` is then `''`/`null`, which the
 * impl maps to `{ alreadySucceeded: true }` (D-12).
 */
interface TransactionResponseWire {
  id?: number;
  reference_id?: string;
  amount_cents?: number;
  transaction_time?: string;
  card_last4?: string;
  card_brand?: string;
  ach_last4?: string;
  status?: string;
}

/**
 * Optional wire→domain field map for the success transaction body. Iterated so
 * each omit-vs-preserve copy is one table row rather than a branch (keeps
 * `adaptTransactionResult` under the complexity ceiling).
 */
const TXN_RESULT_FIELDS: ReadonlyArray<
  readonly [keyof TransactionResponseWire, keyof TransactionResult]
> = [
  ['id', 'id'],
  ['reference_id', 'referenceId'],
  ['amount_cents', 'amountCents'],
  ['transaction_time', 'transactionTime'],
  ['card_last4', 'cardLast4'],
  ['card_brand', 'cardBrand'],
  ['ach_last4', 'achLast4'],
  ['status', 'status'],
];

/** Map the `201` transaction response to the domain result. */
function adaptTransactionResult(
  data: TransactionResponseWire | null | undefined | '',
): TransactionResult {
  // Idempotent replay → empty body → already succeeded (D-12). An empty string
  // or null/undefined body (no `id`) is the replay signal.
  if (!data || typeof data !== 'object' || data.id == null) {
    return { alreadySucceeded: true };
  }
  const result: TransactionResult = { alreadySucceeded: false };
  for (const [wireKey, domainKey] of TXN_RESULT_FIELDS) {
    const value = data[wireKey];
    if (value != null) {
      // Safe: each pair maps a same-typed wire field to its domain field.
      (result as unknown as Record<string, unknown>)[domainKey] = value;
    }
  }
  return result;
}

export const invoiceService: InvoiceService = {
  async fetchInvoiceReport(
    params: InvoiceReportParams,
  ): Promise<InvoiceReportResponse> {
    // Wire shape is the flat Consumer::InvoicesDatatable row (dollar strings,
    // snake_case, optional SPB augmentation). The adapter reshapes it to the
    // cents-based domain InvoiceRow consumed by the UI. See
    // adapters/invoiceRow.ts for the wire/domain contract.
    const { data } = await apiClient.get<InvoiceReportResponseWire>(
      ENDPOINTS.reports.consumerInvoices,
      {
        params: {
          page: params.page + 1,
          per_page: params.rows,
          sort_by: params.sortField,
          sort_desc:
            params.sortOrder === undefined
              ? undefined
              : params.sortOrder === -1,
          query: params.filters?.global?.value,
          balance_due: params.balanceDue || undefined,
          // Server-side filter to SETTLED invoices only. The endpoint honors
          // `:paid` as of api commit 087910992e (PODZ-1761), so the dual-zone
          // InvoicesView's Payment History fetch (paid:true) is filtered on the
          // backend — no client-side row filtering here.
          paid: params.paid || undefined,
        },
        // D-12: forward the caller's AbortSignal so the InvoicesView
        // stale-guard can cancel an in-flight history fetch (axios rejects with
        // a CanceledError the caller swallows via axios.isCancel).
        signal: params.signal,
      },
    );
    rememberInvoiceRows(data);
    return adaptInvoiceReportResponse(data);
  },

  // Phase 5.2 (BILL-02 / D-22): single invoice detail. Hits
  // `GET /v2/invoices/:id` directly — NOT customer-scoped (the server owns
  // `authorize! :read, @invoice`), so we do NOT read customerId here. The wire
  // is the nested `_invoice.json.jbuilder` payload; adaptInvoiceDetail reshapes
  // it to the cents-based domain InvoiceDetail. See adapters/invoiceDetail.ts.
  async getInvoice(id: number, signal?: AbortSignal): Promise<InvoiceDetail> {
    // D-23: forward the caller's AbortSignal so the store's stale-guard can
    // cancel a superseded in-flight detail fetch (axios rejects with a
    // CanceledError the store swallows via axios.isCancel).
    // D-G2 (Phase 05.6): always request grouped line items via
    // `item_grouping=1`. No FeatureFlag/tenant gating — harmless for flat
    // invoices (ungrouped items just gain `type:'lineItem'`+`group_id:null`
    // and render identically). The param rides in the SAME axios options
    // object as `signal`; the endpoint builder stays untouched.
    const { data } = await apiClient.get<InvoiceDetailWire>(
      ENDPOINTS.v2.invoices.get(id),
      { params: { item_grouping: 1 }, signal },
    );
    rememberInvoicePayload(data);
    return adaptInvoiceDetail(data);
  },

  // Phase 12 (TC-718, D-04) — customer work confirmation: POST /v2/invoices/:id/sign.
  // Like getInvoice it is NOT customer-scoped: the server checks the invoice is
  // the signer's own. The body is built ONLY by the buildSignPayload
  // allowlist; the PNG data URL is posted as a string, never fetch()ed (CSP
  // connect-src has no data:). The response body is ignored: the caller
  // reconciles by refetching the invoice (D-05, never optimistic).
  async signInvoice(invoiceId: number, signatureImage: string): Promise<void> {
    await apiClient.post(
      ENDPOINTS.v2.invoices.sign(invoiceId),
      buildSignPayload({ signatureImage }),
      { timeout: SIGN_REQUEST_TIMEOUT_MS },
    );
  },

  // TC-725: the no-login invoice link's sign. Same request as signInvoice, on the
  // scoped payClient with the link session's temp_token (never apiClient).
  async signInvoiceViaLink(
    invoiceId: number,
    signatureImage: string,
    tempToken: string,
  ): Promise<void> {
    await payClient.post(
      ENDPOINTS.v2.invoices.sign(invoiceId),
      buildSignPayload({ signatureImage }),
      { params: { temp_token: tempToken }, timeout: SIGN_REQUEST_TIMEOUT_MS },
    );
  },

  // Phase 8 (PAYLINK-02 / D-06/D-13) — pay-by-link token exchange. POSTs the
  // opaque link token to `POST /v2/pay/:token/exchange` through the dedicated
  // scoped `payClient` (NEVER apiClient) with NO request body and NO credential:
  // a signed-in user's Authorization header on the exchange would trip a 409
  // session_conflict (T-08-01), and payClient never reads stored auth (D-06a).
  // Returns the PayExchangeWire body verbatim — the caller reads
  // `session.temp_token` and threads it into getInvoiceViaPayToken (D-05). A bad
  // token rejects as a typed PayApiError from payClient's own response handler;
  // it never bounces to /login (D-07).
  async exchangePayToken(linkToken: string): Promise<PayExchangeWire> {
    const { data } = await payClient.post<PayExchangeWire>(
      ENDPOINTS.v2.pay.exchange(linkToken),
    );
    rememberPayExchangePayload(data);
    return data;
  },

  // Phase 8 (PAYLINK-16 / D-06b/D-13) — fetch the shared invoice for the pay
  // page. Hits the SAME `GET /v2/invoices/:id` URL the authed getInvoice calls
  // (:130-134), but routes through the scoped `payClient` and carries the session
  // credential as a `?temp_token=` query param alongside `item_grouping=1`. The
  // temp_token is threaded per-call (never a module global — D-05) and propagates
  // verbatim into the params (T-08-12). Reuses `adaptInvoiceDetail` verbatim —
  // the pay variant differs from getInvoice ONLY in the client + the extra
  // temp_token param, so there is no pay-specific re-mapping (DRY). The caller's
  // AbortSignal is forwarded so the view's stale-guard can cancel a superseded
  // fetch.
  async getInvoiceViaPayToken(
    id: number,
    tempToken: string,
    signal?: AbortSignal,
  ): Promise<InvoiceDetail> {
    const { data } = await payClient.get<InvoiceDetailWire>(
      ENDPOINTS.v2.invoices.get(id),
      { params: { item_grouping: 1, temp_token: tempToken }, signal },
    );
    rememberInvoicePayload(data);
    return adaptInvoiceDetail(data);
  },

  // Phase 5.3 (BILL-10 / D-11) — authoritative customer payment-method list +
  // per-key SDK creds. Hits `GET /v2/payment_methods` (VERIFIED 2026-06-05: the
  // route is drawn inside `versioned_api(2)`, so it IS under `/v2` — see
  // endpoints.ts for the route/controller citations).
  // The server filters to the customer-allowed set (Card + ACH only) and returns
  // a PLAIN JSON array (not a jbuilder); adaptPaymentMethods reshapes it to the
  // domain PaymentMethodOption[]. The caller's AbortSignal is forwarded so the
  // store's stale-guard can cancel a superseded fetch (D-12).
  async getPaymentMethods(
    signal?: AbortSignal,
  ): Promise<PaymentMethodOption[]> {
    const { data } = await apiClient.get<PaymentMethodWire[]>(
      ENDPOINTS.paymentMethods,
      { signal },
    );
    return adaptPaymentMethods(data);
  },

  // Phase 5.3 (BILL-04/05/06 / D-01/D-06/D-12) — execute a charge. POSTs the
  // built transaction-request body to `/v2/invoices/:id/transactions` with
  // `nextPaymentKey` as a URL QUERY PARAM (idempotency — there is NO
  // `Idempotency-Key` header; transactions_controller.rb:269). On a `201` with
  // an EMPTY body (idempotent replay) the charge already succeeded on a prior
  // attempt → returns `{ alreadySucceeded: true }`, NOT an error (D-12). Error
  // envelopes (`{message}` / `{status,error,message}`) propagate as the rejected
  // axios error; the caller surfaces `.message`.
  async submitTransaction(
    invoiceId: number,
    body: TransactionRequestBody,
    nextPaymentKey: string,
    signal?: AbortSignal,
  ): Promise<TransactionResult> {
    const { data } = await apiClient.post<TransactionResponseWire | ''>(
      ENDPOINTS.v2.transactions.create(invoiceId),
      body,
      { params: { next_payment_key: nextPaymentKey }, signal },
    );
    return adaptTransactionResult(data);
  },

  // Phase 9 (PAYLINK-05 / D-06 / PODZ-1872 charge transport) — pay-by-link
  // sibling of getPaymentMethods (:185). Hits the SAME `GET /v2/payment_methods`
  // URL the authed call uses, but routes through the scoped `payClient` (NEVER
  // apiClient — a stored Authorization header on an anonymous pay request would
  // trip a 409 session_conflict; T-09-02-S1) and carries the session credential
  // as a `?temp_token=` query param. The temp_token is threaded per-call (never
  // a module global — D-05). Reuses `adaptPaymentMethods` verbatim — the pay
  // variant differs from getPaymentMethods ONLY in the client + the temp_token
  // param, so there is no pay-specific re-mapping (DRY). The Accept version
  // header rides free via the payClient default. The caller's AbortSignal is
  // forwarded so the store's stale-guard can cancel a superseded fetch.
  async getPaymentMethodsViaPayToken(
    tempToken: string,
    signal?: AbortSignal,
  ): Promise<PaymentMethodOption[]> {
    const { data } = await payClient.get<PaymentMethodWire[]>(
      ENDPOINTS.paymentMethods,
      { params: { temp_token: tempToken }, signal },
    );
    return adaptPaymentMethods(data);
  },

  // Phase 9 (PAYLINK-05 / D-06 / CR-01 / PODZ-1872 charge transport) — pay-by-
  // link sibling of submitTransaction (:203). POSTs the built transaction-request
  // body to the SAME `/v2/invoices/:id/transactions` URL, but through the scoped
  // `payClient` (NEVER apiClient — T-09-02-S1) with BOTH `next_payment_key` and
  // the session `temp_token` as URL QUERY PARAMS (there is NO `Idempotency-Key`
  // header; transactions_controller.rb:269). `nextPaymentKey` is
  // server-authoritative and forwarded VERBATIM — the client never mints or
  // rotates it (CR-01); the re-fetch of a fresh key reuses getInvoiceViaPayToken
  // (already parses `.nextPaymentKey`), so there is NO third pay method. On a
  // `201` with an EMPTY body (idempotent replay) the charge already succeeded →
  // returns `{ alreadySucceeded: true }`, NOT an error (D-12). Reuses
  // `adaptTransactionResult` verbatim (no pay-specific re-mapping). Error
  // envelopes propagate as the rejected PayApiError; the caller surfaces
  // `.message`.
  async submitTransactionViaPayToken(
    invoiceId: number,
    body: TransactionRequestBody,
    nextPaymentKey: string,
    tempToken: string,
    signal?: AbortSignal,
  ): Promise<TransactionResult> {
    const { data } = await payClient.post<TransactionResponseWire | ''>(
      ENDPOINTS.v2.transactions.create(invoiceId),
      body,
      {
        params: { next_payment_key: nextPaymentKey, temp_token: tempToken },
        signal,
      },
    );
    return adaptTransactionResult(data);
  },
};
